// @flow
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from "react-router-dom";
import Header from "../Header";
import BackButton from "../../GUI_COMMON/COMPONENTS/BackButton";
import TextInput from "../../GUI_COMMON/COMPONENTS/TextInput";
import LIButton from "../../GUI_COMMON/COMPONENTS/LIButton";
import Popup from "../../GUI_COMMON/COMPONENTS/Popup";
import ErrorPopup from "../../GUI_COMMON/COMPONENTS/ErrorPopup";
import CheckInput from "../../GUI_COMMON/COMPONENTS/CheckInput";
import TextArea from "../../GUI_COMMON/COMPONENTS/TextArea";
import {StoreContext} from "../../GUI_COMMON/SUPPORT/store";
import ArchiverClientApi from "../../API/archiverClientApi";
import ArchiverGuiUtils from "../../SUPPORT/ArchiverGuiUtils";
import LoadingSpinner from "../../GUI_COMMON/COMPONENTS/LoadingSpinner";
import LIUtils from "../../GUI_COMMON/SUPPORT/LIUtils";
import {useCookies} from "react-cookie";
import GuiUtils from "../../GUI_COMMON/SUPPORT/GuiUtils";
import ImageSetterCard from "../ImageSetterCard";
import {ImageInfoStruct} from "../ImageInfoStruct";

const ProfileSetsAdminPage = () => {
    
    // Constants - set at the archiver (must match)
    const PROFILE_TYPE_MAG = 0;
    const PROFILE_TYPE_SAMPLE = 1;
    
    const [globalState] = useContext(StoreContext);
    
    const [curProfileSetDetails, setCurProfileSetDetails] = useState(getDefaultProfileSetDetails());
    const [fullProfileSetList, setFullProfileSetList] = useState([]);
    const [filteredProfileSetList, setFilteredProfileSetList] = useState(null);
    const [sampleProfilesList, setSampleProfilesList] = useState([]);
    const [magProfilesList, setMagProfilesList] = useState([]);
    const [pSetImage, setPSetImage] = useState(new ImageInfoStruct());
    
    const [isDirty, setIsDirty] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [readonly, setReadonly] = useState(false);
    
    const history = useHistory();
    const textFilterRef = useRef(null);
    const { profileSetId } = useParams();           // set in the url
    const [selectedProfileSetId, setSelectedProfileSetId] = 
                            useState(profileSetId?Number(profileSetId):null);
    
    const [cookies] = useCookies();
    
    useEffect( () => {
        loadProfileSets();
        if(!LIUtils.UserHasLevel(cookies,"SERVICE")) { // admins can look but not touch
            setReadonly(true);
        }
    },[]); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        if(fullProfileSetList) {
            setProfileListsFromGlobalState();
            doRealListItemClicked(selectedProfileSetId);  // preselect if the url has something in it.
        }
    },[fullProfileSetList,selectedProfileSetId]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        setProfileListCheckBoxes(curProfileSetDetails?.profileIds);
    }, [curProfileSetDetails]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    //#region LOAD
    
    function loadProfileSets() {
        
        setIsLoading(true);
        setProfileListsFromGlobalState();
        ArchiverClientApi.getProfileSets()
        .then( (profileSetList) => {
            // augment
            profileSetList = augmentProfileSetListWithProfileIds(profileSetList);
            setFullProfileSetList(profileSetList);
        })
        .catch( responseError => {
            ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError);
            setFullProfileSetList([]); // to ensure a refresh on error)
        })
        .finally( () => {
            setIsLoading(false);
        });
    }
    
    function augmentProfileSetListWithProfileIds(profileSetList:[]):[] {
        profileSetList.forEach( (profileSetItem) => {
            augmentProfileSetWithProfileIds(profileSetItem);
        });
        return profileSetList;
    }
    
    function augmentProfileSetWithProfileIds(profileSetItem:{}):{} {
        let profileIds = [];
        profileSetItem.profiles.forEach( (profile) => {
            profileIds.push(profile.id);
        });
        profileSetItem.profileIds = profileIds; // augment with ids.
        return profileSetItem;
    }
    
    function getDefaultProfileSetDetails():{} {
        return(
            {
                id: null,
                name: "",
                description: "",
                enabled: true,
                imageData: null,
                profiles: [],  // real profiles list
                profileIds: []  // just the ids
            }
        );
    }
    
    function setProfileListsFromGlobalState() {
        setMagProfilesList(globalState.profiles.items.filter( (item) => item.type===PROFILE_TYPE_MAG)??[]);
        setSampleProfilesList(globalState.profiles.items.filter( (item) => item.type===PROFILE_TYPE_SAMPLE)??[]);
        resetProfileSetDetailsShown();
    }
    function setProfileListCheckBoxes(profileIds) {

        const workingMagProfilesList = [...magProfilesList];
        const workingSampleProfilesList = [...sampleProfilesList];
        
        // reset
        clearProfileCheckBoxSelections(workingMagProfilesList);
        clearProfileCheckBoxSelections(workingSampleProfilesList);
        
        profileIds.forEach( (profileId) => {
            const magProfile = workingMagProfilesList.find( (item) => item.id===profileId);
            if(magProfile) {
                magProfile.isChecked=true;
            }
            const sampleProfile = workingSampleProfilesList.find( (item) => item.id===profileId);
            if(sampleProfile) {
                sampleProfile.isChecked=true;
            }
        });
        setMagProfilesList(workingMagProfilesList);
        setSampleProfilesList(workingSampleProfilesList);
    }
    
    function clearProfileCheckBoxSelections(profileList:[]):[] {
        profileList.forEach( (item) => {
            item.isChecked = false;
        });
        return profileList;
    }

    function clearDirtyFlags() {
        setIsDirty(false);
        setPSetImage(new ImageInfoStruct()); // reset
    }
    
    //#endregion LOAD
    
    //#region DELETE
    function handleDeleteProfileSetClicked(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // get the # of devices using this profile set (for useful info)
        ArchiverClientApi.getDevicesUsingProfileSet(curProfileSetDetails.id)
        .then( (deviceList:[]) => {
            showDeleteProfileSetPopup(deviceList?.length??undefined);
        })
        .catch( _responseError => {
            showDeleteProfileSetPopup(undefined);
        }) // ignore all errors here
        .finally();
    }
    
    function showDeleteProfileSetPopup(count:number) {

        const countStr = count===undefined?"":`<p>There are ${(count===0?"no":count)} devices using this profile set.  </p>`;
        Popup.showConfirm("Delete Profile?",`<p>This will remove '${curProfileSetDetails.name}' completely from the system.</p>`+
            `<p>${countStr}Devices can continue to use the profile set until their next run is started.</p>`+
            "<p>Are you sure you want to do this?</p>","Yes, Delete it",null,
            () => {
                doProfileSetRemove(curProfileSetDetails.name,curProfileSetDetails.id);
            });
    }
   
    function doProfileSetRemove(profileName, profileSetId) {
        setIsRemoving(true);
        ArchiverClientApi.sendProfileSetRemove(profileSetId)
        .then(() => {
            Popup.showInfo(`ProfileSet '${profileName}' removed`);
            removeProfileSetFromList(profileSetId);
            resetProfileSetDetailsShown();
        })
        .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally(() => {
            setIsRemoving(false);
        });
    }
    
    function removeProfileSetFromList(profileSetId) {
        const newProfileSetList = fullProfileSetList.filter( (item) => item.id!==profileSetId);
        setFullProfileSetList(newProfileSetList);
    }
    //#endregion DELETE
    
    //#region SAVE

    function handleCancelClicked(e) {
        e.preventDefault();
        e.stopPropagation();

        confirmClearDirty();
    }

    function confirmClearDirty() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    resetProfileSetDetailsShown();
                });
        }
        else {
            resetProfileSetDetailsShown(); // just clear what's there
        }
    }
    
    function handleSaveClicked(e,action) {
        e.preventDefault();
        e.stopPropagation();

        if (!checkForValid()) {
            return;
        }

        setIsSaving(true);
        handleProfileSetSave(action);
    }
    
    function handleProfileSetSave(action:string) {
        
        ArchiverClientApi.sendProfileSetUpdate(fixValues())
        .then( (savedProfileSetInfo) => {
            Popup.showInfo(`ProfileSet '${curProfileSetDetails.name}' Saved`);
            clearDirtyFlags();
            savedProfileSetInfo = augmentProfileSetWithProfileIds(savedProfileSetInfo);
            if(action==="ADD") {
                addNewProfileSetToList(savedProfileSetInfo);
            }
            else {
                const newProfileSetList = fullProfileSetList.map( (item) => {
                    if(item.id===savedProfileSetInfo.id) {
                        return savedProfileSetInfo;
                    }
                    return item;
                });
                setFullProfileSetList(newProfileSetList);
                setSelectedProfileSetId(savedProfileSetInfo.id);
            }
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
           setIsSaving(false); 
        });
    }

    function checkForValid():boolean {
        let isValid=true;
        let notValidMsg="";
        if(curProfileSetDetails.name==="") {
            isValid=false;
            notValidMsg="You need to give the profile set a name.";
        }
        else if(curProfileSetDetails.description==="") {
            isValid=false;
            notValidMsg="It's a good idea to put in a useful description";
        }
        else if(!atLeastOneProfileSelected()) {
            isValid=false;
            notValidMsg="You must select at least one profile";
        }

        if(!isValid) {
            ErrorPopup.showError("Validation Problem",notValidMsg);
            return false;
        }
        return true;

    }

    function fixValues():{} {
        return curProfileSetDetails; // nothing to do
    }
    
    //#endregion SAVE
    
    function addNewProfileSetToList(newProfileSetInfo:{}) {
        const newProfileSetList = [...fullProfileSetList];
        newProfileSetList.push(newProfileSetInfo);
        setFullProfileSetList(newProfileSetList);
    }
    function atLeastOneProfileSelected():boolean {
        let foundOne=false;
        magProfilesList.forEach( (item) => {
            if(item.isChecked) {
                foundOne=true;
            }
        });
        sampleProfilesList.forEach( (item) => {
            if(item.isChecked) {
                foundOne=true;
            }
        });
        return foundOne;
    }
    function onChange(e) {
        if(readonly) {
            return;
        }
        const thing = e.target.name;
        const editInfo = {...curProfileSetDetails};
        if(e.target.type==="checkbox") { // this is a checkbox
            editInfo[thing] = e.target.checked;
        }
        else {
            editInfo[thing] = e.target.value;
        }
        setCurProfileSetDetails(editInfo);
        setIsDirty(true);
    }

    //#region FILTERING

    function handleClearFilterClicked() {
        if(textFilterRef.current) {
            textFilterRef.current.value = "";
        }
        setFilteredProfileSetList(null);
    }

    function dumpDataFilter() {
        return (
            <div className="input-group">
                <TextInput refName={textFilterRef} name="textFilter" label="" placeholder="filter" textLeft
                           maxLength={10} onChange={handleDataFilterChanged} widthPels={150}/>
                <div className="input-group-append">
                    <LIButton bgClass="ml-1" faIcon="fa fa-times-circle" small primary onClick={handleClearFilterClicked}/>
                </div>
            </div>
        );
    }

    function handleDataFilterChanged() {
        if(textFilterRef.current) {
            const filterText = textFilterRef.current.value;
            if(filterText==="") {
                setFilteredProfileSetList(null);
            }
            else {
                const filtered = fullProfileSetList.filter( row => {
                    return row["name"].toString().toLowerCase().indexOf(filterText.toLowerCase().trim()) !== -1;

                });
                setFilteredProfileSetList(filtered);
            }
        }
    }


    //#endregion FILTERING
    
    function updateUrlLink(toThis) {
        history.replace(toThis);
    }
    function resetProfileSetDetailsShown() {
        setCurProfileSetDetails(getDefaultProfileSetDetails());
        clearDirtyFlags();
        updateUrlLink("/admin/profile_sets"); // blank

        // focus the first input field
        const nameEl:HTMLInputElement = document.getElementsByName("name")[0];
        if(nameEl) {
            nameEl.focus();
        }
    }
    
    //#region CLICK_HANDLERS
    function handleListItemClicked(item) {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    resetProfileSetDetailsShown();
                    doRealListItemClicked(item?.id);
                });
        }
        else {
            doRealListItemClicked(item?.id);
        }
    }
    function doRealListItemClicked(itemId) {
        const profileSet = fullProfileSetList.find(i => i.id===itemId);
        if(!profileSet) {
            resetProfileSetDetailsShown();
        }
        else {
            setCurProfileSetDetails(profileSet);
            // set the image into the image card
            const imageInfo = new ImageInfoStruct();
            imageInfo.SetupBasedOnEncodedBytes(profileSet.imageData);
            setPSetImage(imageInfo);
            updateUrlLink(`/admin/profile_sets/${profileSet.id}`);
        }
    }
    function handleBackClicked() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    history.push("/adminPage");
                });
        }
        else {
            history.goBack();
        }
        
    }
    function handleMagProfilesCBChanged(e,profileId) {
        if(readonly) {
            return;
        }
        const updatedParentList = updateList(e, magProfilesList,  profileId);
        setMagProfilesList(updatedParentList);
        setIsDirty(true);
    }
    
    function handleSampleProfilesCBChanged(e,profileId) {
        if(readonly) {
            return;
        }
        const updatedParentList = updateList(e, sampleProfilesList,  profileId);
        setSampleProfilesList(updatedParentList);
        setIsDirty(true);
    }

    function updateList(e,parentList,profileId) {
        const all = [...parentList];
        const editInfo = {...curProfileSetDetails};
        const p = parentList.find(i => i.id===profileId);
        if(p) {
            p.isChecked=e.target.checked;
            // update the actual profile set
            if(p.isChecked && !editInfo.profileIds.includes(profileId)) {
                editInfo.profileIds.push(profileId);
            }
            if(!p.isChecked && editInfo.profileIds.includes(profileId)) {
                const idx = editInfo.profileIds.indexOf(profileId);
                editInfo.profileIds.splice(idx, 1);
            }
        }
        setCurProfileSetDetails(editInfo);
        return all;
    }

    function handleCBClicked(e) {
        e.stopPropagation();
    }
    //#endregion CLICK_HANDLERS
    
    //#region SHOW
    
    function showMagProfilesList() {
        
        let resultList = magProfilesList?.map((item,index) => {
                let extraClass = item.enabled?"":" li-strike-through";
                const validations = LIUtils.FindValidationsGivenIRs(item.inspectionRegions)??[];
                const validationsStr = validations.join(", ");

                return <li key={index} className={"list-group-item li-pointer "+extraClass} 
                           onClick={() => handleGoToProfilesClicked(item.id)}>
                    <input name={"magProfile"+index} type="checkbox" className="mr-3"
                           checked={item?.isChecked??false} 
                           onChange={(e)=>handleMagProfilesCBChanged(e,item.id)}
                           onClick={handleCBClicked} />
                    {item.name??""}<br/>
                    <div className="ml-5 li-font-tiny li-fg-primary font-weight-bold">
                        {item.comment}
                    </div>
                    <div className="text-right li-font-tiny">
                        <span>Holds: <b>
                            {GuiUtils.GetSampleTypeNameGivenId(item.subType, globalState.sampleTypes.items)}
                        </b></span><br/>
                        <div>Validations: <b>{validationsStr}</b></div>
                    </div>
                </li>;
            }
        );
        if(!resultList || resultList.length===0) {
            resultList = "no mag profiles found";
        }
        return (<div className="">
                <h4>
                    Magazine Profiles <LIButton label="" small primary faIcon="fa fa-external-link" bgClass="ml-3"
                          onClick={handleGoToProfilesClicked}/>
                </h4>

                <div className="li-scrollable-at-500 ">
                    <ul className="list-group-flush li-list-group-w-hover px-5">
                        {resultList}
                    </ul>
                </div>
            </div>);
    }

    function handleGoToProfilesClicked(profileId:number=0) {
        const gotoLink = "/admin/profiles" + (profileId>0?"/"+profileId:"");
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    history.push(gotoLink);
                });
        }
        else {
            history.push(gotoLink);
        }
    }

    function showSampleProfilesList() {
        let resultList = sampleProfilesList?.map((item, index) => {
                let extraClass = item.enabled ? "" : " li-strike-through";
                const validations = LIUtils.FindValidationsGivenIRs(item.inspectionRegions)??[];
                const validationsStr = validations.join(", ");

                return <li key={index} className={"list-group-item li-pointer " + extraClass}
                           onClick={() => handleGoToProfilesClicked(item.id)}>
                    <input name={"sampleProfile"+index} type="checkbox" className="mr-3" 
                           checked={item?.isChecked??false}
                           onChange={(e) => handleSampleProfilesCBChanged(e, item.id)}
                           onClick={handleCBClicked} value={index}/>
                    {item.name}<br/>
                    <div className="ml-5 li-font-tiny li-fg-primary font-weight-bold">
                        {item.comment}
                    </div>
                    <div className="text-right li-font-tiny">
                        <span>Unique Barcodes? <b>{item.barcodesAreUnique.toString()}</b></span><br/>
                        <span>Case Mapped External? <b>{item.isCaseMappedExternal.toString()}</b></span><br/>
                        <div>Validations: <b>{validationsStr}</b></div>
                    </div>
                </li>;
            }
        );
        if (!resultList || resultList.length === 0) {
            resultList = "no sample profiles found";
        }
        return (<div className="">
                <h4>
                    Sample Profiles <LIButton label="" small primary faIcon="fa fa-external-link" bgClass="ml-3"
                                                onClick={handleGoToProfilesClicked}/>
                </h4>
                <div className="li-scrollable-at-500">
                    <ul className="list-group-flush li-list-group-w-hover px-5">
                        {resultList}
                    </ul>
                </div>
            </div>);
    }
    
    function showProfileSetsList(fullProfileSetList) {
        if (isLoading) {
            return <div className="mt-4 pr-4 w-30 li-border-right-light"><LoadingSpinner medium center/></div>;
        }

        const showingProfileSetItems = filteredProfileSetList ?? fullProfileSetList;
        let resultList = showingProfileSetItems?.map((item, index) => {
            const isDisabledFont = (item.enabled ? "" : "li-strike-through");
            const isActive = curProfileSetDetails?.id === item.id;
            return <li key={index} className={"list-group-item li-pointer " + isDisabledFont+ " " + (isActive ? "li-bg-light" : "")}
                       onClick={() => handleListItemClicked(item)}>{item.name}</li>;
        });
        if (!resultList || resultList.length === 0) {
            resultList = "no sets found";
        }

        return (<div className="mt-4 pr-4 w-30 li-border-right-light">
                <h4>Profile Sets</h4>
                <div className="ml-4 my-3">
                    {dumpDataFilter()}
                </div>
                <div className="li-scrollable-at-750 li-border-light-top p-0 m-0">
                    <ul className="list-group-flush li-list-group-w-hover pl-0">
                        {resultList}
                    </ul>
                    <div className="li-border-line-light"/>
                </div>
                <LIButton small primary label="" bgClass={isRemoving ? "m-3" : "fa fa-trash my-3 ml-3"}
                          disabled={readonly || curProfileSetDetails?.id === null}
                          onClick={handleDeleteProfileSetClicked} showBusy={isRemoving}/>
                <LIButton small primary label="" bgClass={isRemoving ? "" : "fa fa-plus m-3"}
                          disabled={readonly}
                          onClick={confirmClearDirty} showBusy={false}/>
            </div>);
    }

    
    //#endregion SHOW
    
    function setNewImageInfo(dataBytes) {
        
        const updatedImage = new ImageInfoStruct(dataBytes, true, null);
        setPSetImage(updatedImage);

        // and the profile set is actually dirty now too
        const editInfo = {...curProfileSetDetails};
        editInfo.imageData= updatedImage.encodedBytes; // encoded string here
        setCurProfileSetDetails(editInfo);
        setIsDirty(true);
    }
    
    return (
        <div className="container">
            <Header hideAdmin/>
            <div className="li-header-row">
                <BackButton onClick={handleBackClicked}/>
                <h1 className="ml-4 d-inline-block align-middle">Profile Sets Administration</h1>
            </div>
            
            <div className="row my-1">
                {readonly && <div className="w-100 p-1 li-bg-warn li-fg-white font-weight-bold li-font-small text-center">[*** read only (service level needed to edit) ***]</div>}
            </div>
            
            <div className="row">
                {/* LEFT SIDE PICK LIST */}
                {showProfileSetsList(fullProfileSetList)}

                {/* RIGHT SIDE FORM */}
                <div className="pl-4 w-60">
                    <div
                        className="li-fg-slightly-muted li-font-small text-right">[Id:{curProfileSetDetails?.id ?? "-"}]
                    </div>
                    <div className="row m-0 p-0 justify-content-end mt-2">
                        <CheckInput label="Enabled" name="enabled" checked={curProfileSetDetails.enabled}
                                    onChanged={(e) => onChange(e)}/>
                    </div>
                    <div className="row p-0 m-0 justify-content-between">
                        {/* right side, row #1 */}
                        <TextInput name="name" label="Name" onChange={(e) => onChange(e)}
                                   placeholder="" textLeft maxLength={32}
                                   value={curProfileSetDetails?.name ?? ""}
                                   required/>
                    </div>
                    {/* right side, row #2 */}
                    <TextArea name="description" label="Description" value={curProfileSetDetails?.description ?? ""}
                              readonly={false}
                              onChange={(e) => onChange(e)} rows={2} maxLength={60}/>

                    {/* right side, row #3 */}
                    <div className="">
                        <label className="li-form-label mr-3">Image</label>
                        <ImageSetterCard disabled={readonly || isSaving || curProfileSetDetails.name === ""}
                                            imageInfo={pSetImage} onSaveClicked={setNewImageInfo}/>
                    </div>

                    {/* right side, row #4 */}
                    <div className="mt-4"/>
                    {showMagProfilesList()}
                    <div className="li-border-line-light mb-2"/>
                    {showSampleProfilesList()}

                    {/* BUTTON ROW */}
                    <div className="text-center my-5">
                        <LIButton key="cancel" name="cancel" label={isDirty ? "Cancel" : "Clear"} bgClass="mr-4" primary
                                  onClick={handleCancelClicked}/>
                        <LIButton key="save" name="save" label={curProfileSetDetails?.id === null ? "ADD" : "UPDATE"}
                                  secondary disabled={!isDirty || isSaving}
                                  onClick={(e) => handleSaveClicked(e, curProfileSetDetails?.id === null ? "ADD" : "UPDATE")}
                                  showBusy={isSaving}/>
                    </div>
                </div>
            </div>

        </div>);
}

ProfileSetsAdminPage.propTypes = {};

export default ProfileSetsAdminPage;